// src/state/useQuizNavigation.ts
import { StepId } from "@/domains/quiz/StepDefinitions";
import { StepMachine } from "@/domains/quiz/StepMachine";
import { useQuizContext } from "./QuizContext";
import { writeQuizStepToURL } from "./urlState";

export function useQuizNavigation() {
  const { state, dispatch } = useQuizContext();

  const machine = new StepMachine({
    perDogStep: state.stepState.perDogStep as Record<string, StepId>,
    globalStep: state.stepState.globalStep as StepId,
  });

  return {
    getDogStep: (dogId: string) => machine.getDogStep(dogId),

    nextDogStep: (dogId: string, form: any) => {
      const res = machine.nextDogStep(dogId, form);
      if (res.step) {
        dispatch({ type: "SET_DOG_STEP", dogId, step: res.step });
        dispatch({ type: "SET_GLOBAL_STEP", step: res.step });

        writeQuizStepToURL(res.step as StepId, dogId);
      }
      return res;
    },

    prevDogStep: (dogId: string) => {
      const res = machine.prevDogStep(dogId);
      if (res.step) {
        dispatch({ type: "SET_DOG_STEP", dogId, step: res.step });
        dispatch({ type: "SET_GLOBAL_STEP", step: res.step });

        writeQuizStepToURL(res.step as StepId, dogId);
      }
      return res;
    },

    /**
     * Multi-dog stacked flow: all dogs move through the question screens together.
     * Advances from the first dog's current step and applies the result to every dog.
     */
    nextAllStep: (dogs: { id: string; form: any }[]) => {
      const first = dogs[0];
      if (!first) return { step: null };
      const res = machine.nextDogStep(first.id, first.form);
      if (res.step) {
        dogs.forEach((d) => dispatch({ type: "SET_DOG_STEP", dogId: d.id, step: res.step as StepId }));
        dispatch({ type: "SET_GLOBAL_STEP", step: res.step });
        writeQuizStepToURL(res.step as StepId, first.id);
      }
      return res;
    },

    prevAllStep: (dogs: { id: string; form: any }[]) => {
      const first = dogs[0];
      if (!first) return { step: null };
      const res = machine.prevDogStep(first.id);
      if (res.step) {
        dogs.forEach((d) => dispatch({ type: "SET_DOG_STEP", dogId: d.id, step: res.step as StepId }));
        dispatch({ type: "SET_GLOBAL_STEP", step: res.step });
        writeQuizStepToURL(res.step as StepId, first.id);
      }
      return res;
    },

    setDogStep: (dogId: string, step: StepId) => {
      dispatch({ type: "SET_DOG_STEP", dogId, step });
      dispatch({ type: "SET_GLOBAL_STEP", step });
      writeQuizStepToURL(step, dogId);
    },

    getGlobalStep: () => machine.getGlobalStep(),

    nextGlobalStep: () => {
      const res = machine.nextGlobalStep();
      if (res.step) {
        dispatch({ type: "SET_GLOBAL_STEP", step: res.step });
      }
      return res;
    },

    prevGlobalStep: () => {
      const res = machine.prevGlobalStep();
      if (res.step) {
        dispatch({ type: "SET_GLOBAL_STEP", step: res.step });
      }
      return res;
    },
  };
}
